import {
  useState,
} from 'react';
import {
  Callout,
  Figure,
  Modal,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatCount,
  formatDay,
  useLoad,
  SortTh,
  TimeSeries,
  TableActions,
  useSort,
  daysBetween,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
  type PlatformActivityDay,
} from '../../client.ts';
import {
  platformDashboard,
} from '../../screens.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';
import type { RouteId } from '../../routes.ts';
import { OperatorApprovalsPanel } from './OperatorApprovalsPanel.tsx';

function KpiGroup({
  title,
  figures,
  onOpen,
}: {
  title: string;
  figures: readonly {
    label: string;
    value: string;
    tone?: 'note' | 'caution';
    spark?: readonly number[];
    /** Which activity this card counts, on the cards that open their days. */
    metric?: 'clicks' | 'registrations' | 'ftds';
  }[];
  /**
   * Given a figure, what opening that card should do — or null for a card
   * with nothing behind it, which keeps its plain unpressable box rather
   * than opening an empty popup.
   */
  onOpen?: (figure: { label: string; metric?: 'clicks' | 'registrations' | 'ftds' }) => (() => void) | null;
}) {
  return (
    <Panel title={title}>
      <div className="figures">
        {figures.map((f) => {
          const open = onOpen?.(f) ?? null;
          return (
            <Figure
              key={f.label}
              // Every caution here is a warning about the platform — a chain
              // that does not verify, consumers that stopped, a figure that
              // could not be read — and none is money that might still move,
              // so it is attention, not provisional.
              view={{ label: f.label, value: f.value, provisional: false }}
              attention={f.tone === 'caution'}
              {...(f.spark ? { spark: f.spark } : {})}
              {...(open ? { onOpen: open, openLabel: t('overview.figure.openLabel') } : {})}
            />
          );
        })}
      </div>
    </Panel>
  );
}

/**
 * What a platform activity card opens: the days it is the sum of.
 *
 * `platform_daily_kpis` is a platform-wide rollup with no operator column, so
 * "which operator contributed what" is not a question this data can answer —
 * that would need a new cross-tenant query, and a cross-tenant read on this
 * console has to be logged per operator it touches (§3.9e). What it can answer
 * honestly is the arithmetic: this figure is these days added up, and the
 * table totals to prove it.
 */
function DaysBehindTheCard({ metric, label, daily, onClose }: {
  metric: 'clicks' | 'registrations' | 'ftds';
  label: string;
  daily: readonly PlatformActivityDay[];
  onClose: () => void;
}) {
  const rows = daily.filter((d) => d[metric] > 0);
  const { rows: ordered, sort } = useSort(rows, {
    day: (r) => r.day,
    value: (r) => r[metric],
    affiliates: (r) => r.affiliates,
  }, { key: 'day' });
  const total = daily.reduce((n, d) => n + d[metric], 0);

  return (
    <Modal title={t('overview.days.title', { label })} onClose={onClose} wide>
      {rows.length === 0 ? (
        <Empty>{t('overview.days.empty')}</Empty>
      ) : (
        <>
          <TableActions
            title={t('overview.days.exportTitle', { label })}
            headers={[t('common.day'), label, t('overview.days.affiliatesActive')]}
            keys={['day', metric, 'affiliates']}
            rows={ordered.map((r) => [r.day, String(r[metric]), String(r.affiliates)])}
            filename={`platform-${metric}-by-day`}
          />
          <TableScroll>
            <table className="banded-by-index">
              <thead>
                <tr>
                  <SortTh sort={sort} col="day">{t('common.day')}</SortTh>
                  <SortTh sort={sort} col="value" align="num">{label}</SortTh>
                  <SortTh sort={sort} col="affiliates" align="num">{t('overview.days.affiliates')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r, i) => (
                  <tr key={r.day} className={i % 2 === 1 ? 'row-alt' : ''}>
                    <td className="num faint">{formatDay(r.day)}</td>
                    <td className="num">{formatCount(r[metric])}</td>
                    <td className="num faint">{formatCount(r.affiliates)}</td>
                  </tr>
                ))}
              </tbody>
              {/* The days have to add up to the card that opened them. Days
                  with none are left out of the rows and still counted here. */}
              <tfoot>
                <tr>
                  <th>{t('overview.days.total')}</th>
                  <td className="num">{formatCount(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </TableScroll>
          <Callout>{t('overview.days.note')}</Callout>
        </>
      )}
    </Modal>
  );
}

/**
 * The AffDice SuperAdmin's own headline — cross-tenant health and billing,
 * never an operator's own affiliate-marketing KPIs (§3.10b). Three of the
 * five groups below reuse the exact calls the Markets, Audit chain and
 * Consumers tabs already make; tenant portfolio, platform billing and
 * platform-wide activity (§39.2a Layer C) are the genuinely new queries —
 * nothing else on this console answers them.
 */
export function Dashboard({ api, onNavigate }: { api: PlatformApi; onNavigate: (r: RouteId) => void }) {
  const now = new Date();
  const overview = useLoad(() => api.overview(), []);
  /* Which activity card is open — above the loading guards, it is a hook. */
  const [openCard, setOpenCard] = useState<'clicks' | 'registrations' | 'ftds' | null>(null);
  const opsSummary = useLoad(() => api.operatorsSummary(), []);
  const auditVerify = useLoad(() => api.verifyChain(), []);
  const consumersLoad = useLoad(() => api.consumers(), []);
  const activity = useLoad(() => api.activitySummary(), []);
  const billing = useLoad(() => api.billingSummary(), []);
  const pending = useLoad(() => api.pendingOperators(), []);
  const showAside = pending.state === 'ready' && pending.value.items.length > 0;

  if (
    overview.state === 'loading' || opsSummary.state === 'loading' ||
    auditVerify.state === 'loading' || consumersLoad.state === 'loading' || activity.state === 'loading' ||
    billing.state === 'loading'
  ) {
    return <Loading rows={4} />;
  }
  if (overview.state === 'error') return <Failure error={overview.error} />;
  if (opsSummary.state === 'error') return <Failure error={opsSummary.error} />;
  if (auditVerify.state === 'error') return <Failure error={auditVerify.error} />;
  if (consumersLoad.state === 'error') return <Failure error={consumersLoad.error} />;
  if (activity.state === 'error') return <Failure error={activity.error} />;
  if (billing.state === 'error') return <Failure error={billing.error} />;

  const view = platformDashboard(
    {
      overview: overview.value,
      operatorsByState: opsSummary.value,
      chainResult: auditVerify.value,
      consumerRows: consumersLoad.value.items,
      consumersCheckedAt: consumersLoad.value.checked_at,
      activity: activity.value,
      billingRows: billing.value.by_currency,
    },
    now,
  );

  const daily = activity.value.data?.daily ?? [];
  const activityByDay = new Map(daily.map((d) => [d.day, d]));
  const activityDays = daily.length > 0
    ? daysBetween(daily[0]!.day, daily[daily.length - 1]!.day)
    : [];

  return (
    <>
      <div className="page-head">
        <h1>{t('nav.overview')}</h1>
        <span className="updated">{view.updated}</span>
      </div>

      <div className={`dashboard-layout${showAside ? ' has-aside' : ''}`}>
        <div className="dashboard-main">
          <KpiGroup title={t('overview.group.tenantPortfolio')} figures={view.tenantPortfolio} />
          <KpiGroup title={t('overview.group.goLive')} figures={view.goLive} />
          <KpiGroup title={t('overview.group.health')} figures={view.health} />
          {/*
            * The three activity cards open the days they are the sum of. Each
            * card says which activity it counts; its label is translated and
            * carries the range ("Clicks (30d)"), so it is not what is matched.
            */}
          <KpiGroup
            title={t('overview.group.activity')}
            figures={view.activity}
            onOpen={(figure) => {
              const metric = figure.metric ?? null;
              // Nothing to show behind a card whose figure could not be read.
              if (!metric || daily.length === 0) return null;
              return () => setOpenCard(metric);
            }}
          />
          {openCard ? (
            <DaysBehindTheCard
              metric={openCard}
              label={
                openCard === 'clicks' ? t('overview.metric.clicks')
                : openCard === 'registrations' ? t('overview.metric.registrations') : t('overview.metric.ftds')
              }
              daily={daily}
              onClose={() => setOpenCard(null)}
            />
          ) : null}
          {/* §39.2a — said once, under the group it explains, not repeated per card. */}
          {view.activityNote ? <Callout tone="note">{view.activityNote}</Callout> : null}

          {/*
            * The days behind the activity cards (owner, 2026-09-29: the same
            * chart on every console). Drawn from `platform_daily_kpis`, the
            * rollup the cards above are already totalled from, so the two
            * cannot disagree.
            *
            * The rollup answers only for the days it has rows for, so the
            * range is filled from its own first and last day rather than from
            * a picker this screen does not have.
            */}
          {activityDays.length > 1 ? (
            <Panel title={t('overview.chart.title')}>
              <TimeSeries
                days={activityDays}
                leftLabel={t('overview.metric.clicks')}
                rightLabel={t('overview.metric.registrations')}
                emptyNote={t('overview.chart.empty')}
                series={[
                  {
                    key: 'clicks',
                    label: t('overview.metric.clicks'),
                    values: activityDays.map((d) => activityByDay.get(d)?.clicks ?? 0),
                    axis: 'left',
                    dashed: true,
                    colour: 'var(--chart-clicks)',
                  },
                  {
                    key: 'registrations',
                    label: t('overview.metric.registrations'),
                    values: activityDays.map((d) => activityByDay.get(d)?.registrations ?? 0),
                    axis: 'right',
                    colour: 'var(--chart-signups)',
                  },
                ]}
              />
            </Panel>
          ) : null}

          <Panel title={t('overview.billing.title')}>
            {view.billing.length === 0 ? (
              <Empty>{t('overview.billing.empty')}</Empty>
            ) : (
              <Sorted rows={view.billing} by={{ currency: (b) => sortableCell(b.currency), issued: (b) => sortableCell(b.issued), paid: (b) => sortableCell(b.paid), outstanding: (b) => sortableCell(b.outstanding) }}>
                {(ordered, sort) => (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <SortTh sort={sort} col="currency">{t('common.currency')}</SortTh>
                      <SortTh sort={sort} col="issued" align="num">{t('overview.billing.issued')}</SortTh>
                      <SortTh sort={sort} col="paid" align="num">{t('overview.billing.paid')}</SortTh>
                      <SortTh sort={sort} col="outstanding" align="num">{t('overview.billing.outstanding')}</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((b) => (
                      <tr key={b.currency}>
                        <td>{b.currency}</td>
                        <td className="num money">{b.issued}</td>
                        <td className="num money">{b.paid}</td>
                        <td className="num money">{b.outstanding}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
                )}
              </Sorted>
            )}
            <Callout>{view.billingNote}</Callout>
          </Panel>

          <div className="filters-row">
            <button className="copy" onClick={() => onNavigate('operators')}>{t('overview.openOperators')}</button>
            <button className="copy" onClick={() => onNavigate('consumers')}>{t('overview.openConsumers')}</button>
          </div>
        </div>

        {showAside ? (
          <aside className="dashboard-aside" aria-label={t('overview.actionQueue')}>
            <OperatorApprovalsPanel api={api} />
          </aside>
        ) : null}
      </div>
    </>
  );
}
